'use client'

import { useState, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { HelpCircle } from 'lucide-react'
import { usePermissions } from '@/hooks/use-permissions'
import { useAuth } from '@/hooks/use-auth'
import { formatCredits } from '@/lib/billing/utils/credits'
import { formatMicroUsd } from '@/lib/billing/utils/format'
import { PlansTab } from './plans-tab'
import { TenantsTab } from './tenants-tab'
import { SettingsTab } from './settings-tab'
import { ElevenLabsPool } from './elevenlabs-pool'
import { NotificationsTab } from './notifications-tab'

type BillingTab = 'plans' | 'pool' | 'tenants' | 'settings' | 'notifications'

interface AdminOverview {
  tenantsByChannel: { stripe: number; shopify: number; total: number }
  subscriptionStats: { active: number; trialing: number; past_due: number; canceled: number; byProvider: { stripe: number; shopify: number; manual: number } }
  walletStats: { totalBalanceMicrousd: number; count: number; depleted: number }
  usageStats30d: {
    textCharges: number; voiceCharges: number; embeddingCharges: number
    totalCharges: number
    /**
     * Provider cost of the 30d wallet-debited rows plus the calls the platform paid
     * for on tenants' behalf. The primary number on the «30d Cost» card; mirrors the
     * per-tenant Cost column in the Tenants tab.
     */
    totalProviderCost: number
    textCount: number; voiceCount: number; embeddingCount: number
  }
  unreadNotifications: number
}

/**
 * Tooltip-wrapped HelpCircle reused by the overview cards. Keeps the inline
 * markup short and prevents copy-paste drift between Total Balance and
 * 30d Cost (the two cards that need a definition tooltip).
 */
function MetricHelp({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-muted-foreground hover:text-foreground"
          aria-label={`${label} tooltip`}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        {children}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Build the «N text / M voice / K emb» counts line for the 30d card.
 * Skips zero-count types so the line stays readable when one channel is idle
 * (a tenant with only voice usage shouldn't read «0 text / 12 voice» — drop
 * the leading zero).
 */
function formatUsageCounts(usage: AdminOverview['usageStats30d']): string {
  const parts: string[] = []
  if (usage.textCount > 0) parts.push(`${usage.textCount} text`)
  if (usage.voiceCount > 0) parts.push(`${usage.voiceCount} voice`)
  if (usage.embeddingCount > 0) parts.push(`${usage.embeddingCount} emb`)
  return parts.length > 0 ? parts.join(' / ') : 'no usage'
}

const TAB_ORDER: BillingTab[] = ['plans', 'pool', 'tenants', 'settings', 'notifications']

export function BillingPage({ defaultTab }: { defaultTab?: string }) {
  const { can } = usePermissions()
  const { fetchWithAuth } = useAuth()

  // Per-tab visibility. `tenants` is additionally reachable by a
  // `billing.manage_tenants` grantee (acts on that list — channel conversion /
  // revert-to-manual) or a read-only `billing.view_tenants` grantee (sees the
  // list without the Plan change / Actions columns — gated inside TenantsTab);
  // the other tabs stay view_all/super-only. super_user satisfies every `can()`
  // via the wildcard, so it still sees all.
  const canViewAll = can('billing.view_all')
  const tabVisible: Record<BillingTab, boolean> = {
    plans: canViewAll,
    pool: canViewAll,
    tenants: canViewAll || can('billing.manage_tenants') || can('billing.view_tenants'),
    settings: canViewAll,
    notifications: canViewAll,
  }
  const visibleTabs = TAB_ORDER.filter((t) => tabVisible[t])

  const [activeTab, setActiveTab] = useState<BillingTab>(() => {
    // Honour ?tab=… when it points at a tab the user may actually see; the URL
    // is the source of truth (deep links / the page never passes defaultTab).
    // Otherwise land on the first visible tab.
    const fromUrl =
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('tab') as BillingTab | null)
        : null
    const requested = fromUrl || (defaultTab as BillingTab | undefined) || null
    if (requested && tabVisible[requested]) return requested
    return visibleTabs[0] ?? 'plans'
  })
  const [editingPlan, setEditingPlan] = useState(false)
  const [overview, setOverview] = useState<AdminOverview | null>(null)

  useEffect(() => {
    // Overview (summary cards + Billing Notifications signal) is VIEW_ALL-only.
    // Skip the guaranteed-403 request for narrower grants, and clear any stale
    // payload if the user is live-downgraded out of view_all without a remount.
    if (!canViewAll) {
      setOverview(null)
      return
    }
    fetchWithAuth('/api/admin/billing/overview')
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (data) setOverview(data) })
      .catch(() => {})
  }, [fetchWithAuth, canViewAll])

  const handleTabChange = (value: string) => {
    setActiveTab(value as BillingTab)
    const url = new URL(window.location.href)
    url.searchParams.set('tab', value)
    window.history.replaceState({}, '', url.toString())
  }

  const canManagePlans = can('billing.manage_plans')

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div hidden={editingPlan}>
        <h1 className="text-2xl font-bold break-words">Billing</h1>
        <p className="text-muted-foreground text-sm">Manage plans, subscriptions, and voice provider pool.</p>
      </div>

      {/* Admin Overview Summary — VIEW_ALL-only (see the overview fetch guard). */}
      {canViewAll && overview && !editingPlan && (
        <TooltipProvider delayDuration={150}>
          <div className="grid gap-3 md:grid-cols-5">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-xs text-muted-foreground">Tenants</div>
                <div className="text-xl font-bold">{overview.tenantsByChannel.total}</div>
                <div className="text-xs text-muted-foreground">
                  {overview.tenantsByChannel.stripe} <span>Stripe /</span> {overview.tenantsByChannel.shopify} <span>Shopify</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-xs text-muted-foreground">Subscriptions</div>
                <div className="text-xl font-bold">{overview.subscriptionStats.active}</div>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {overview.subscriptionStats.past_due > 0 && (
                    <Badge variant="destructive" className="text-xs">{overview.subscriptionStats.past_due} <span>past due</span></Badge>
                  )}
                  {overview.subscriptionStats.byProvider.manual > 0 && (
                    <Badge variant="outline" className="text-xs">{overview.subscriptionStats.byProvider.manual} <span>manual</span></Badge>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <span>Total Balance</span>
                  <MetricHelp label="Total Balance">
                    <span>Sum of every tenant&apos;s wallet balance — including manual, trial, and canceled subscriptions. Should match the total of the</span> <em>Balance</em> <span>column in the Tenants tab below.</span>
                  </MetricHelp>
                </div>
                <div className="text-xl font-bold font-mono break-words">{formatCredits(overview.walletStats.totalBalanceMicrousd)}</div>
                {overview.walletStats.depleted > 0 && (
                  <div className="text-xs text-destructive">{overview.walletStats.depleted} <span>depleted</span></div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <span>30d Cost</span>
                  <MetricHelp label="30d Cost">
                    <span>Provider cost (COGS) we paid Anthropic / OpenAI / ElevenLabs / Twilio for tenant usage</span> <strong>over the last 30 days</strong><span>, the calls the platform paid for on tenants&apos; behalf included. The smaller</span> <em>spent</em> <span>line is what tenants paid us out of their wallets for that same usage — same shape as the Cost column in the Tenants tab. The Tenants tab itself shows lifetime totals; this card is the rolling 30-day window.</span>
                  </MetricHelp>
                </div>
                <div className="text-xl font-bold font-mono break-words">{formatMicroUsd(overview.usageStats30d.totalProviderCost)}</div>
                <div className="text-xs text-muted-foreground/80">
                  <span>spent</span> {formatCredits(overview.usageStats30d.totalCharges)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatUsageCounts(overview.usageStats30d)}
                </div>
              </CardContent>
            </Card>
            <Card
              className="cursor-pointer transition-colors hover:bg-muted/40"
              onClick={() => handleTabChange('notifications')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTabChange('notifications') }}
            >
              <CardContent className="pt-4 pb-3">
                <div className="text-xs text-muted-foreground">Notifications</div>
                <div className="text-xl font-bold">{overview.unreadNotifications}</div>
                <div className="text-xs text-muted-foreground">unread — click to view</div>
              </CardContent>
            </Card>
          </div>
        </TooltipProvider>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className={editingPlan ? 'hidden' : undefined}>
          {tabVisible.plans && <TabsTrigger value="plans">Plans</TabsTrigger>}
          {tabVisible.pool && <TabsTrigger value="pool">Voice Pool</TabsTrigger>}
          {tabVisible.tenants && <TabsTrigger value="tenants">Tenants</TabsTrigger>}
          {tabVisible.settings && <TabsTrigger value="settings">Settings</TabsTrigger>}
          {tabVisible.notifications && (
            <TabsTrigger value="notifications">
              <span>Notifications</span>
              {overview && overview.unreadNotifications > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {overview.unreadNotifications}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {tabVisible.plans && (
          <TabsContent value="plans" className="mt-6">
            <PlansTab canManage={canManagePlans} onEditingChange={setEditingPlan} />
          </TabsContent>
        )}

        {tabVisible.pool && (
          <TabsContent value="pool" className="mt-6">
            <ElevenLabsPool />
          </TabsContent>
        )}

        {tabVisible.tenants && (
          <TabsContent value="tenants" className="mt-6">
            <TenantsTab />
          </TabsContent>
        )}

        {tabVisible.settings && (
          <TabsContent value="settings" className="mt-6">
            <SettingsTab />
          </TabsContent>
        )}

        {tabVisible.notifications && (
          <TabsContent value="notifications" className="mt-6">
            <NotificationsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
